import { TitleButton } from '@/common/buttons/TitleButton';
import { CartesianChart } from '@/common/dashboard/charts/CartesianChart';
import { DashboardCard } from '@/common/dashboard/DashboardCard';
import { XAxisButton } from '@/common/dashboard/XAxisButton';
import { useForm } from '@/common/form/FormContext';
import { far } from '@awesome.me/kit-b4fd369ea4/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { useMemo, useState } from 'react';
import { McdDispOutcomeDetailGrid } from './McdDispOutcomeDetailGrid';
import { useDispReqParam } from './useDispReqParam';

// Reverse lookup from a clicked series' display name back to its backend bucket code; both measures share the labels
const labelToBucket = {
  Created: 'CREATED',
  Accepted: 'ACCEPTED',
  'Rejected (Paid)': 'REJECTED_PAID',
  'Rejected (Due)': 'REJECTED_DUE',
  'Outstanding Disputes': 'OUTSTANDING',
  'URA Revisions': 'URA_REVISION',
};

// The kit has no column-chart icon, so the bar chart is turned upright
const viewOptions = [
  { value: 'bar', icon: far.faChartBar, transform: { rotate: 270, flipX: true }, toolTip: 'Bar Chart' },
  { value: 'line', icon: far.faChartLine, toolTip: 'Line Chart' },
];

// URA Revisions move no units, so they only appear under $
const measureOptions = [
  { value: 'AMT', label: '$', toolTip: 'Amounts', yLabel: 'Disputed Amount Magnitude ($)' },
  { value: 'UNITS', label: 'Units', toolTip: 'Units', yLabel: 'Disputed Units Magnitude' },
];

export const McdDispOutcomeTrendCard = ({ xAxisOptions, dispCodeCount }) => {
  const { formValues, assignValue } = useForm();
  const [view, setView] = useState('bar');
  const [drillCategory, setDrillCategory] = useState(null);
  const [drillBucket, setDrillBucket] = useState(null);

  // A view saved before an axis was removed (e.g. Contract Type) falls back to the first axis
  const xAxisOption = xAxisOptions.find((option) => option.code === formValues.xAxis) ?? xAxisOptions[0];
  const xAxis = xAxisOption.code;

  const dispReqParam = useDispReqParam(dispCodeCount);
  // Outcome and measure are applied client-side since the plugin always returns every series
  const reqParam = useMemo(
    () => ({ ...dispReqParam, xAxis }),
    // drillCategory isn't sent, but forces a refetch when switching the card between chart and grid plugins
    [dispReqParam, xAxis, drillCategory],
  );

  const drillIn = (point) => {
    // Unmatched rows have no single value to drill into
    if (point.category === '(None)') return;
    setDrillCategory(point.category);
    setDrillBucket(labelToBucket[point.series?.name] ?? null);
  };

  const backToChart = () => {
    setDrillCategory(null);
    setDrillBucket(null);
  };

  const measureOption = measureOptions.find((option) => option.value === formValues.measure) ?? measureOptions[0];
  const axisLabel = xAxisOption.axisLabel;

  return (
    <DashboardCard
      title={drillCategory ? 'Disputed Invoice Lines' : undefined}
      description={
        'Displays disputed ' +
        measureOption.toolTip.toLowerCase() +
        ' by ' +
        axisLabel +
        ', split Created vs. Accepted/Rejected (Paid)/Rejected (Due), with what is still Outstanding and signed URA Revisions'
      }
      module="sc"
      plugin={drillCategory ? 'MCD_DISP_OUTCOME_DETAIL_DATA_PLUGIN' : 'MCD_DISP_OUTCOME_TREND_DATA_PLUGIN'}
      reqParam={reqParam}
      buttons={
        drillCategory ?
          <TitleButton icon={far.faArrowLeft} toolTip="Back to Chart" onClick={backToChart} />
        : <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <XAxisButton options={xAxisOptions} />
            <ToggleButtonGroup
              exclusive
              size="small"
              value={measureOption.value}
              onChange={(event, value) => value && assignValue('measure', value)}
            >
              {measureOptions.map((option) => (
                <ToggleButton
                  key={option.value}
                  value={option.value}
                  title={option.toolTip}
                  sx={{ textTransform: 'none', minWidth: 36 }}
                >
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <ToggleButtonGroup exclusive size="small" value={view} onChange={(event, value) => value && setView(value)}>
              {viewOptions.map((option) => (
                <ToggleButton key={option.value} value={option.value} title={option.toolTip}>
                  {/* The span gives the icon the text's line height, so these match the $/Units buttons */}
                  <span>
                    <FontAwesomeIcon icon={option.icon} transform={option.transform} />
                  </span>
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
      }
    >
      {(data) =>
        drillCategory ?
          <McdDispOutcomeDetailGrid rows={data} xAxis={xAxis} category={drillCategory} bucket={drillBucket} />
        : <CartesianChart
            data={{
              ...data,
              series: (
                data.series?.filter(
                  (s) => s.measure === measureOption.value && formValues.bucket?.includes(s.bucket),
                ) ?? []
              ).map((s) => ({
                ...s,
                type: view === 'line' ? 'line' : 'column',
              })),
            }}
            onClick={(point) => drillIn(point)}
            legend="top"
            xLabel={axisLabel}
            yLabel={measureOption.yLabel}
            markers={view === 'line'}
            allowNegative
            rotateXLabels
          />
      }
    </DashboardCard>
  );
};
